import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box
} from '@mui/material';
import { Add, Remove, Fullscreen, FullscreenExit, Edit } from '@mui/icons-material';
import { Node, Edge } from 'reactflow';

interface Props {
  open: boolean;
  onClose: () => void;
  nodes: Node[];
  edges: Edge[];
  onUpdateNodes: (nodes: Node[]) => void;
}

const ShowTableModal: React.FC<Props> = ({ open, onClose, nodes, edges, onUpdateNodes }) => {
  const [editableNodes, setEditableNodes] = useState<Node[]>([]);
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [isEditable, setIsEditable] = useState(false);

  useEffect(() => {
    setEditableNodes(nodes.map((n) => ({ ...n })));
  }, [nodes]);

  const handleFieldChange = (id: string, field: string, value: string) => {
    setEditableNodes((prev) =>
      prev.map((node) =>
        node.id === id ? { ...node, data: { ...node.data, [field]: value } } : node
      )
    );
  };

  const handleButtonOrOptionChange = (nodeId: string, index: number, value: string, fieldType: 'buttons' | 'options') => {
    setEditableNodes((prev) =>
      prev.map((node) => {
        if (node.id !== nodeId) return node;

        const updatedField = Array.isArray(node.data?.[fieldType])
          ? [...node.data[fieldType]]
          : [];

        updatedField[index] = value;

        return {
          ...node,
          data: {
            ...node.data,
            [fieldType]: updatedField
          }
        };
      })
    );
  };

  const getPrevNodes = (nodeId: string) =>
    edges.filter((edge) => edge.target === nodeId).map((edge) => edge.source);

  const getNextNodeForButton = (nodeId: string, itemLabel: any) => {
    const label = String(itemLabel || '');

    const matches = edges.filter((e) => {
      if (e.source !== nodeId) return false;
      const handle = e.sourceHandle || '';
      return handle.toLowerCase().includes(label.toLowerCase());
    });
    return matches.map((e) => e.target).join(', ');
  };

  const getHeaderType = (node: Node) => {
    const rawType = node.data?.headerType?.value || '';
    return rawType.toLowerCase();
  };

  const getHeaderContent = (node: Node) => {
    const type = getHeaderType(node);
    if (
      ["document", "video", "image", "url"].includes(type) &&
      node.data?.media?.s3_object?.url
    ) {
      return node.data.media.s3_object.url;
    }
    return node.data?.header || '';
  };

  const handleSave = () => {
    onUpdateNodes(editableNodes);
    setIsEditable(false);
    onClose();
  };

  const handleZoomIn = () => setZoom((prev) => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setZoom((prev) => Math.max(prev - 0.1, 0.5));
  const handleToggleFullscreen = () => setFullscreen((prev) => !prev);

  const columnStyle = {
    minWidth: 120,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullscreen} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Bot Flow Table View
        <Box>
          <IconButton onClick={handleZoomOut}><Remove /></IconButton>
          <IconButton onClick={handleZoomIn}><Add /></IconButton>
          <IconButton onClick={handleToggleFullscreen}>
            {fullscreen ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            sx={{ ml: 2 }}
            onClick={() => setIsEditable((prev) => !prev)}
          >
            {isEditable ? 'View Mode' : 'Edit Mode'}
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ height: fullscreen ? '100%' : 600, padding: 0 }}>
        <Box
          sx={{
            transform: `scale(${zoom})`,
            transformOrigin: 'top left',
            overflow: 'auto',
            height: '100%',
            padding: 1,
          }}
        >
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={columnStyle}>ID</TableCell>
                  <TableCell sx={columnStyle}>Type</TableCell>
                  <TableCell sx={columnStyle}>Node Count</TableCell>
                  <TableCell sx={columnStyle}>Header Type</TableCell>
                  <TableCell sx={columnStyle}>Header</TableCell>
                  <TableCell sx={{ ...columnStyle, minWidth: 450 }}>Body</TableCell>
                  <TableCell sx={columnStyle}>Footer</TableCell>
                  <TableCell sx={columnStyle}>Button / Option</TableCell>
                  <TableCell sx={{ minWidth: 80 }}>Prev</TableCell>
                  <TableCell sx={{ minWidth: 80 }}>Next</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editableNodes.flatMap((node) => {
                  const buttons = Array.isArray(node.data?.buttons) ? node.data.buttons : [];
                  const options = Array.isArray(node.data?.options)
                    ? node.data.options.flatMap((opt: any) =>
                      Array.isArray(opt.options) ? opt.options : []
                    )
                    : [];

                  // const items = buttons.length > 0 ? buttons : options.length > 0 ? options : [''];

                  const items = buttons.length > 0
                    ? buttons
                    : options.length > 0
                      ? options.map((opt: any) => (typeof opt === 'string' ? opt : (opt?.label || '')))
                      : [''];


                  return items.map((item: string, idx: number) => (
                    <TableRow key={`${node.id}-${item}-${idx}`}>
                      <TableCell>{node.id}</TableCell>
                      <TableCell>{node.type}</TableCell>
                      <TableCell>{node.data?.nodeCount}</TableCell>
                      <TableCell>{getHeaderType(node)}</TableCell>
                      <TableCell>
                        {isEditable ? (
                          <TextField
                            fullWidth
                            variant="standard"
                            value={getHeaderContent(node)}
                            onChange={(e) =>
                              handleFieldChange(node.id, 'header', e.target.value)
                            }
                          />
                        ) : (
                          getHeaderContent(node)
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditable ? (
                          <TextField
                            fullWidth
                            multiline
                            minRows={2}
                            variant="standard"
                            value={node.data?.body || ''}
                            error={(node.data?.body || '').length > 3000}
                            helperText={
                              (node.data?.body || '').length > 3000
                                ? 'Body exceeds 3000 characters!'
                                : `${(node.data?.body || '').length}/3000`
                            }
                            onChange={(e) => {
                              const newValue = e.target.value;
                              if (newValue.length <= 3000) {
                                handleFieldChange(node.id, 'body', newValue);
                              }
                            }}
                          />
                        ) : (
                          (node.data?.body || '').substring(0, 3000)
                        )}
                      </TableCell>

                      <TableCell>
                        {isEditable ? (
                          <TextField
                            fullWidth
                            variant="standard"
                            value={node.data?.footer || ''}
                            onChange={(e) =>
                              handleFieldChange(node.id, 'footer', e.target.value)
                            }
                          />
                        ) : (
                          node.data?.footer
                        )}
                      </TableCell>
                      <TableCell>
                        {isEditable ? (
                          <TextField
                            fullWidth
                            variant="standard"
                            value={item}
                            onChange={(e) => {
                              if (buttons.length > 0) {
                                handleButtonOrOptionChange(node.id, idx, e.target.value, 'buttons');
                              } else {
                                handleButtonOrOptionChange(node.id, idx, e.target.value, 'options');
                              }
                            }}
                          />
                        ) : (
                          item
                        )}
                      </TableCell>
                      <TableCell>{getPrevNodes(node.id).join(', ')}</TableCell>
                      <TableCell>{getNextNodeForButton(node.id, item)}</TableCell>
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {isEditable && <Button variant="contained" onClick={handleSave}>Apply Changes</Button>}
      </DialogActions>
    </Dialog>
  );
};

export default ShowTableModal;






// import React, { useState, useEffect } from 'react';
// import {
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Button,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   TextField,
//   Paper
// } from '@mui/material';
// import { Node, Edge } from 'reactflow';

// interface Props {
//   open: boolean;
//   onClose: () => void;
//   nodes: Node[];
//   edges: Edge[];
//   onUpdateNodes: (nodes: Node[]) => void;
// }

// const ShowTableModal: React.FC<Props> = ({ open, onClose, nodes, edges, onUpdateNodes }) => {
//   const [editableNodes, setEditableNodes] = useState<Node[]>([]);
//   const [focusedCell, setFocusedCell] = useState<{ id: string; key: string } | null>(null);

//   useEffect(() => {
//     setEditableNodes(nodes.map((n) => ({ ...n })));
//   }, [nodes]);

//   const handleCellChange = (id: string, field: string, value: string) => {
//     setEditableNodes((prev) =>
//       prev.map((node) =>
//         node.id === id
//           ? {
//               ...node,
//               data: {
//                 ...node.data,
//                 [field]: value
//               }
//             }
//           : node
//       )
//     );
//   };

//   const excludedKeys = [
//     'parentResults',
//     'params',
//     'onChange',
//     'onNodesDelete',
//     'headerTypeIsUrl'
//   ];

//   const allKeys = Array.from(
//     new Set(nodes.flatMap((node) => Object.keys(node.data || {})))
//   ).filter((key) => !excludedKeys.includes(key));

//   const handleSave = () => {
//     onUpdateNodes(editableNodes);
//     onClose();
//   };

//   const renderValue = (value: any): string => {
//     if (typeof value === 'object' && value !== null) {
//       if ('url' in value) return value.url;
//       return JSON.stringify(value);
//     }
//     return value ?? '';
//   };

//   return (
//     <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
//       <DialogTitle>Bot Flow Table View</DialogTitle>
//       <DialogContent>
//         <TableContainer component={Paper}>
//           <Table size="small">
//             <TableHead>
//               <TableRow>
//                 <TableCell>ID</TableCell>
//                 <TableCell>Type</TableCell>
//                 {allKeys.map((key) => (
//                   <TableCell key={key}>{key}</TableCell>
//                 ))}
//               </TableRow>
//             </TableHead>
//             <TableBody>
//               {editableNodes.map((node) => (
//                 <TableRow key={node.id} style={{ height: '60px' }}> {/* fixed row height */}
//                   <TableCell>{node.id}</TableCell>
//                   <TableCell>{node.type}</TableCell>
//                   {allKeys.map((key) => {
//                     const isFocused = focusedCell?.id === node.id && focusedCell.key === key;
//                     return (
//                       <TableCell key={key}>
//                         <TextField
//                           variant="standard"
//                           value={renderValue(node.data?.[key])}
//                           onFocus={() => setFocusedCell({ id: node.id, key })}
//                           onBlur={() => setFocusedCell(null)}
//                           multiline={isFocused}
//                           minRows={isFocused ? 4 : 1}
//                           fullWidth
//                           InputProps={{
//                             sx: {
//                               minWidth: isFocused ? '300px' : '100px',
//                               transition: 'all 0.2s ease-in-out',
//                               overflow: 'hidden'
//                             }
//                           }}
//                           sx={{
//                             textarea: {
//                               resize: 'none',
//                               overflow: 'hidden'
//                             }
//                           }}
//                           onChange={(e) => handleCellChange(node.id, key, e.target.value)}
//                         />
//                       </TableCell>
//                     );
//                   })}
//                 </TableRow>
//               ))}
//             </TableBody>
//           </Table>
//         </TableContainer>
//       </DialogContent>
//       <DialogActions>
//         <Button onClick={onClose}>Cancel</Button>
//         <Button variant="contained" onClick={handleSave}>Apply Changes</Button>
//       </DialogActions>
//     </Dialog>
//   );
// };

// export default ShowTableModal;

